import { useNavigate } from '@tanstack/react-router'
import JSONCrush from 'jsoncrush'
import { useCallback } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import { CrushedParamError } from '~/modules/error-boundary'

// interface DrillDownParams {
//   d: Range <-- Date range
//   cId: ID[] <-- Channel IDs
//   tId: ID[] <-- Tag IDs
//   uId: ID[] <-- User IDs
// }

const ROOT_KEY = 'r'

export const getCrushedParam = (key = ROOT_KEY): string | null => {
  const query = new URLSearchParams(window.location.search)
  const value = query.get(key)
  return value
}

export const parseCrushedParam = <R>(value: string | null): void | Record<string, R> => {
  if (value) {
    const parsedValue = JSON.parse(JSONCrush.uncrush(value))
    return parsedValue
  }
}

export const updateParam = <J>(key: string, value: J): string => {
  const params = parseCrushedParam(getCrushedParam()) || {}
  params[key] = value
  return JSONCrush.crush(JSON.stringify(params))
}

const isGeneric = <J>(value: unknown): value is J => typeof value === 'object'

const useCrushedParam = <J, RJ>(
  key: string,
  defaultValue: J,
  formatter?: (value: RJ) => J,
): [J, typeof crushParam] => {
  const navigate = useNavigate()

  const getParam = () => {
    try {
      const valueStr = getCrushedParam()
      const value = parseCrushedParam<RJ>(valueStr)
      if (value && value[key]) {
        const v = value[key]
        if (formatter) {
          return formatter(v)
        }
        if (isGeneric<J>(v)) {
          return v
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new CrushedParamError(e.message)
      } else {
        throw Error('unknown error occurred while parsing crushed params')
      }
    }

    return defaultValue
  }

  const crushParam = useCallback(
    (value: J, opts?: { replace?: boolean }) => {
      const params = updateParam<J>(key, value)
      navigate({
        // @ts-ignore
        search: (prev) => ({ ...prev, r: params }),
        replace: opts?.replace,
      })
    },
    [key, navigate],
  )

  //const [param, setParam] = useState<J>(updateParams)

  const param = getParam()

  //if (key === 'd') console.log('searchParams', key, getCrushedParam()[key])

  useEffectOnce(() => {
    crushParam(param, { replace: true })
  })

  // useUpdateEffect(() => {
  //   const nextParams = updateParams()
  //   if (
  //     Array.isArray(nextParams) &&
  //     Array.isArray(param) &&
  //     !isEqualArray(nextParams, param)
  //   ) {
  //     setParam(nextParams)
  //   }
  // })

  const handleParamChange = useCallback(
    (value: J) => {
      crushParam(value)
    },
    [crushParam],
  )

  return [param, handleParamChange]
}

export default useCrushedParam
